/* Guitar: standard tuning, fretboard positions, chord shapes and voicings. */
import type { ChordQuality, ChordRef } from '../dsp/chord';
import { getSettings } from '../store/state';
import { chordSymbol, parseChord, QUAL } from './chords';
import { letter, mod, pcName } from './notes';

/** Open-string MIDI notes; index 0 = 6th string (low E) ... index 5 = 1st string. */
export const TUNING: readonly number[] = [40, 45, 50, 55, 59, 64];
/** Turkish string numbering: 6 = thickest (low E), 1 = thinnest. */
export const stringNo = (idx: number): number => 6 - idx;
export const stringIdx = (no: number): number => 6 - no;
export const openString = (no: number): number => TUNING[stringIdx(no)] as number;

/** "6. tel (kalın Mi)" */
export function stringLabel(no: number): string {
  const open = openString(no);
  const extra = no === 6 ? 'kalın ' : no === 1 ? 'ince ' : '';
  const style = getSettings().noteNames === 'letter' ? 'letter' : 'solfege';
  return `${no}. tel (${extra}${pcName(mod(open, 12), { style })})`;
}

export interface FretPos {
  /** string number 1..6 */
  s: number;
  /** fret */
  f: number;
}

/** Every place a MIDI note can be played up to `maxFret`. */
export function positions(midi: number, maxFret = 12): FretPos[] {
  const out: FretPos[] = [];
  for (let i = 0; i < 6; i++) {
    const f = midi - (TUNING[i] as number);
    if (f >= 0 && f <= maxFret) out.push({ s: stringNo(i), f });
  }
  return out;
}

export const fretMidi = (string: number, fret: number): number => openString(string) + fret;

/** Open chord shapes, strings 6 -> 1, x = muted. */
export const SHAPES: Record<string, string> = {
  C: 'x32010',
  D: 'xx0232',
  E: '022100',
  F: '133211',
  G: '320003',
  A: 'x02220',
  B: 'x24442',
  Am: 'x02210',
  Bm: 'x24432',
  Cm: 'x35543',
  Dm: 'xx0231',
  Em: '022000',
  Fm: '133111',
  Gm: '355333',
  'F#m': '244222',
  'C#m': 'x46654',
  E7: '020100',
  A7: 'x02020',
  D7: 'xx0212',
  G7: '320001',
  C7: 'x32310',
  B7: 'x21202',
  F7: '131211',
  Cmaj7: 'x32000',
  Fmaj7: 'xx3210',
  Gmaj7: '320002',
  Dmaj7: 'xx0222',
  Amaj7: 'x02120',
  Emaj7: '021100',
  Am7: 'x02010',
  Dm7: 'xx0211',
  Em7: '022030',
  Bm7: 'x20202',
  'F#m7': '242222',
  Asus2: 'x02200',
  Asus4: 'x02230',
  Dsus2: 'xx0230',
  Dsus4: 'xx0233',
  Esus4: '022200',
  Bb: 'x13331',
  Bdim: 'x2343x',
};

type Fret = number | null;

// Movable barre templates relative to the root fret
const BARRE: Record<'E' | 'A', Partial<Record<ChordQuality, readonly Fret[]>>> = {
  E: {
    '': [0, 2, 2, 1, 0, 0],
    m: [0, 2, 2, 0, 0, 0],
    '7': [0, 2, 0, 1, 0, 0],
    m7: [0, 2, 0, 0, 0, 0],
    maj7: [0, null, 1, 1, 0, null],
    sus4: [0, 2, 2, 2, 0, 0],
    '5': [0, 2, 2, null, null, null],
    m6: [0, 2, 2, 0, 2, 0],
  },
  A: {
    '': [null, 0, 2, 2, 2, 0],
    m: [null, 0, 2, 2, 1, 0],
    '7': [null, 0, 2, 0, 2, 0],
    m7: [null, 0, 2, 0, 1, 0],
    maj7: [null, 0, 2, 1, 2, 0],
    sus2: [null, 0, 2, 2, 0, 0],
    sus4: [null, 0, 2, 2, 3, 0],
    dim: [null, 0, 1, 2, 1, null],
    aug: [null, 0, 3, 2, 2, 1],
    m7b5: [null, 0, 1, 0, 1, null],
    '6': [null, 0, 2, 2, 2, 2],
    '5': [null, 0, 2, 2, null, null],
    dim7: [null, 0, 1, 2, 1, 2],
    add9: [null, 0, 2, 4, 2, 0],
  },
};

const parseShape = (str: string): Fret[] => str.split('').map((c) => (c === 'x' ? null : Number.parseInt(c, 36)));

export interface ChordShape {
  /** One entry per string, 6 -> 1; null = muted. */
  frets: Fret[];
  open: boolean;
  barre?: number;
  form?: 'E' | 'A';
}

/** Open shape when one exists, otherwise the lowest E- or A-form barre shape. */
export function shapeFor(root: number, q: ChordQuality, sym?: string | null): ChordShape | null {
  const s = sym || chordSymbol(root, q);
  const keys = [s, letter(root, false) + q, letter(root, true) + q];
  for (const k of keys) {
    const shape = SHAPES[k];
    if (shape) return { frets: parseShape(shape), open: true };
  }
  const cands: { form: 'E' | 'A'; base: number; tpl: readonly Fret[] }[] = [];
  for (const form of ['E', 'A'] as const) {
    const tpl = BARRE[form][q];
    if (!tpl) continue;
    const base = mod(root - (form === 'E' ? 4 : 9), 12);
    cands.push({ form, base, tpl });
  }
  if (!cands.length) return null;
  cands.sort((a, b) => a.base - b.base);
  const c = cands[0] as (typeof cands)[number];
  return {
    frets: c.tpl.map((v) => (v == null ? null : v + c.base)),
    open: false,
    barre: Math.max(c.base, 0),
    form: c.form,
  };
}

export function shapeMidis(shape: ChordShape): number[] {
  const out: number[] = [];
  shape.frets.forEach((f, i) => {
    if (f != null) out.push((TUNING[i] as number) + f);
  });
  return out;
}

/** MIDI notes (low -> high) to strum for a chord symbol or chord ref. */
export function voicing(chord: string | ChordRef): number[] {
  const c = typeof chord === 'string' ? parseChord(chord) : chord;
  if (!c) return [];
  const sh = shapeFor(c.root, c.q, typeof chord === 'string' ? chord : null);
  if (sh) return shapeMidis(sh);
  // fallback: bass root + close voicing
  const bass = 40 + mod(c.root - 4, 12);
  const out = [bass];
  let n = bass + 12;
  for (const iv of QUAL[c.q]) {
    let m = bass + 12 + iv;
    while (m < n) m += 12;
    out.push(m);
    n = m;
  }
  return out;
}
